import { EditIcon } from 'evergreen-ui';
import { createSelector } from '@reduxjs/toolkit';
import { dcpcApi } from './dcpcApi';
import { METEO_CODE_CONFINES } from './meteoCodeConfinesApi';
import axios from "axios";
import {hostWithPrefix} from "../../Features/Constructor/common/ApiCommon";

const METEOCODES_TAG = 'MeteoCodes';
dcpcApi.enhanceEndpoints({ addTagTypes: [METEOCODES_TAG] });

const meteoCodesApi = dcpcApi.injectEndpoints({
  endpoints: (builder) => ({
    getMeteoCodes: builder.query({
      query: ({ page = 0, size }) => ({
        url: `meteo-codes/?page=${page}${size ? `&size=${size}` : ''}`,
        method: 'GET',
      }),
      providesTags: [METEOCODES_TAG],
    }),

    getMeteocodeByCodeString: builder.query({
      query: (code) => `meteo-codes/${code}/string`,
      providesTags: [METEOCODES_TAG],
    }),

    getMeteocodesByCodesStrings: builder.query({
      queryFn: async (codes, { dispatch }) => {
        const promises = codes.map((code) => dispatch(meteoCodesApi.endpoints.getMeteocodeByCodeString.initiate(code)));
        promises.forEach((result) => result.unsubscribe());

        const results = await Promise.all(promises);

        if (results.some((result) => result.error)) {
          const errors = [];

          results.forEach((result) => {
            if (result.error) {
              errors.push(result.error);
            }
          });

          return { error: errors };
        }

        const data = results.map((result) => result.data);
        return { data };
      },
      providesTags: [METEOCODES_TAG],
    }),

    getAllMeteoCodes: builder.query({
      query: () => 'meteo-codes',
      providesTags: [METEOCODES_TAG],
    }),
    getMeteoCode: builder.query({
      query: (id) => ({
        url: `meteo-codes/${id}`,
        method: 'GET',
      }),
      // extraOptions: {
      //   toasterMessage: '123123123123',
      // },
      providesTags: [METEOCODES_TAG],
    }),
    getMeteoCodeConfines: builder.query({
      query: (id) => ({
        url: `meteo-codes/${id}/confines`,
        method: 'GET',
      }),
      providesTags: [METEOCODES_TAG, METEO_CODE_CONFINES],
    }),
    updateMeteoCode: builder.mutation({
      query: ({ id, body }) => ({
        url: 'meteo-codes',
        method: id ? 'PUT' : 'POST',
        body,
      }),
      invalidatesTags: [METEOCODES_TAG],
    }),
    generateMeteoCode: builder.mutation({
      query: (args) => ({
        url: 'meteo-codes/generate',
        method: 'POST',
        body: args,
        xmlFetcher: true,
      }),
    }),

    getMeteoCodesByDatasourceId: builder.query({
      query: (id) => ({
        url: `datasource/${id}/meteo-codes`,
        method: 'GET',
      }),
      // providesTags: () => [METEOCODES_INVALIDATE_TAG],
    }),
    getMeteocodesForTimeSeriesUnion: builder.query({
      query: (id) => ({
        url: `meteo-codes/datasources/${id}/union`,
        method: 'GET',
      }),
      // providesTags: () => [METEOCODES_INVALIDATE_TAG],
    }),
    getMeteocodesByStationIdUnion: builder.query({
      query: (stationId) => ({
        url: `meteo-codes/stations/${stationId}/union`,
        method: 'GET',
      }),
      // providesTags: () => [METEOCODES_INVALIDATE_TAG],
    }),
  }),
});

export function getNeededMeteoCodesByDatasourceId(datasourceId) {
  return  axios({
    url: 'http://' + hostWithPrefix +  `/meteocodes/by-datasource/${datasourceId}`,
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
    },
    responseType: 'json',
  })
}

export function getNeededMeteoCodesByStationId(stationId, stationDatasourceId) {
  if (stationDatasourceId === null || stationDatasourceId === undefined) {
    return  axios({
      url: 'http://' + hostWithPrefix +  `/meteocodes/by-station/${stationId}`,
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
      responseType: 'json',
    })
  }
  return axios({
    url: 'http://' + hostWithPrefix +  `/meteocodes/by-datasource/${stationDatasourceId}`,
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
    },
    responseType: 'json',
  })
}

export const {
  useGetAllMeteoCodesQuery,
  useGetMeteoCodesQuery,
  useGetMeteoCodeQuery,
  useGetMeteoCodeConfinesQuery,
  useGetMeteoCodesByDatasourceIdQuery,
  useUpdateMeteoCodeMutation,
  useGenerateMeteoCodeMutation,
  useGetMeteocodesForTimeSeriesUnionQuery,
  useGetMeteocodesByStationIdUnionQuery,
  useGetMeteocodeByCodeStringQuery,
  useGetMeteocodesByCodesStringsQuery,
} = meteoCodesApi;

export const meteoCodesSelect = (state) => meteoCodesApi.endpoints.getAllMeteoCodes.select()(state);
export const meteoCodesSelector = (state) => meteoCodesSelect(state).data || [];

export const meteoCodesByDsSelect = (state) => meteoCodesApi.endpoints.getMeteoCodesByDatasourceId.select()(state);
export const meteoCodesByDsSelector = (state) => meteoCodesByDsSelect(state).data || [];

export const meteoCodesFilterByCodesExcludes = (codes, searchText) => createSelector(
  meteoCodesSelector,
  (meteocodes) => meteocodes.filter(({ code }) => !codes.includes(code))
    .filter(({ codeString, description }) => {
      if (searchText === '') return true;
      return codeString.toUpperCase().includes(searchText.toUpperCase()) || description.toUpperCase().includes(searchText.toUpperCase());
    }),
);

export const meteoCodesFilterByCodesIncludes = (codes) => createSelector(
  meteoCodesSelector,
  (meteocodes) => meteocodes.filter(({ code }) => codes.includes(code)),
);

export const filterMeteocodesBySearchTextSelector = createSelector(
  meteoCodesSelector,
  (_, searchText) => searchText,
  (meteocodes, searchText) => ({
    columns: [
      {
        label: 'ID',
      },
      {
        label: 'Код',
      },
      {
        label: 'Наименование',
      },
      {
        label: 'Описание',
      },
      {
        label: 'Единица измерения',
      },
    ],
    rows: meteocodes.reduce((acc, cur) => {
      const {
        meteoCodeHash, name, description, meteoCodeString, unit,
      } = cur;
      const row = [meteoCodeHash, meteoCodeHash, meteoCodeString, name, description, unit].map((v) => v || '');
      if (row.filter((v, idx) => idx !== 0 && v !== null && v.toString().toUpperCase().includes(searchText.toUpperCase())).length > 0) {
        acc.push(row);
      }
      return acc;
    }, []),
    rowButtons: [
      {
        label: 'Редактировать',
        to: (id) => `${id}`,
        icon: EditIcon,
      },
    ],
    createLink: {
      label: 'Создать метеокод',
    },
  }),
);

export const meteocodesBySearchTextSelector = (searchText) => (state) => filterMeteocodesBySearchTextSelector(state, searchText);
